import { useCallback, useMemo } from 'react';
import { ThirdPartyErrorMessage } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/third-party/onederful.pb';
import { InsuranceCoverageResponse } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v2/insurance_verification_adapter.pb';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { BannerNotification, Button, Text } from '@frontend/design-system';
import { useInsuranceTabShallowStore } from '../stores';

type InsuranceSetProviderPinBannerProps = {
  insuranceVerificationCoverageDetails: InsuranceCoverageResponse;
  locationId: string;
};

const providerPinErrorMessage = 'provider_pin_required';

export const InsuranceSetProviderPinBanner = ({
  insuranceVerificationCoverageDetails,
  locationId,
}: InsuranceSetProviderPinBannerProps) => {
  const { t } = useTranslation();
  const { setCredentialManagementTab } = useInsuranceTabShallowStore('setCredentialManagementTab');
  const { selectedLocationIds } = useAppScopeStore();

  const isSingleLocation = selectedLocationIds.length === 1;

  const errorDetails = insuranceVerificationCoverageDetails?.errorDetails || {};
  const errorMessageDetails = (errorDetails as any).thirdPartyErrorDetails as ThirdPartyErrorMessage;
  const errorMessage = errorMessageDetails?.message || '';

  const hasProviderPinError = errorMessage === providerPinErrorMessage;

  const navigateToPayerConnectionManagement = useCallback(() => {
    setCredentialManagementTab('payers');
    if (isSingleLocation) {
      window.location.hash = `settings/insurance/payer-connection-management`;
    } else {
      window.location.hash = `settings/insurance/payer-connection-management/${locationId}`;
    }
  }, [setCredentialManagementTab, locationId, isSingleLocation]);

  const payerNotConnectedMessage = useMemo(
    () => (
      <>
        <Text>{t('Please add the NPI and Provider PIN under the Payer section in the Payer Management screen.')}</Text>
        <Button css={{ padding: 0 }} iconName='plus' variant='tertiary' onClick={navigateToPayerConnectionManagement}>
          {t('Add Payer NPI')}
        </Button>
      </>
    ),
    [t, navigateToPayerConnectionManagement]
  );

  if (!hasProviderPinError) return null;

  return (
    <BannerNotification css={{ marginBottom: theme.spacing(2) }} status='warn' message={payerNotConnectedMessage} />
  );
};
